
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useVitaStorage } from "@/hooks/use-vita-storage";
import { VitaContent } from "@/types/wellness";

export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  last_message?: string;
}

export function useVitaChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [credits, setCredits] = useState<{ used: number; limit: number } | null>(null);
  const { user } = useAuth();
  const { saveVitaContent } = useVitaStorage();
  const { toast } = useToast();

  const fetchChats = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('vita_chats')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setChats(data as ChatSession[] || []);
    } catch (error) {
      console.error("Error fetching chats:", error);
      toast({
        title: "Error",
        description: "Failed to load your chat history",
        type: "error"
      });
    }
  };

  const fetchMessages = async (chatId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('vita_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      const formattedMessages = (data || []).map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role as "user" | "assistant",
        timestamp: new Date(msg.created_at)
      }));
      
      setMessages(formattedMessages);
      setCurrentChatId(chatId);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast({
        title: "Error",
        description: "Failed to load chat messages",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createNewChat = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('vita_chats')
        .insert({
          user_id: user.id,
          title: `Chat ${new Date().toLocaleDateString()}`
        })
        .select()
        .single();

      if (error) throw error;
      
      setCurrentChatId(data.id);
      setMessages([]);
      await fetchChats();
      
      return data.id;
    } catch (error) {
      console.error("Error creating new chat:", error);
      toast({
        title: "Error",
        description: "Failed to create a new chat",
        type: "error"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatTitle = async (chatId: string, newTitle: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('vita_chats')
        .update({ title: newTitle })
        .eq('id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      await fetchChats();
    } catch (error) {
      console.error("Error updating chat title:", error);
      toast({
        title: "Error",
        description: "Failed to update chat title",
        type: "error"
      });
    }
  };

  const deleteChat = async (chatId: string) => {
    if (!user) return;

    try {
      const { error: messagesError } = await supabase
        .from('vita_messages')
        .delete()
        .eq('chat_id', chatId);

      if (messagesError) throw messagesError;

      const { error: chatError } = await supabase
        .from('vita_chats')
        .delete()
        .eq('id', chatId)
        .eq('user_id', user.id);

      if (chatError) throw chatError;
      
      if (currentChatId === chatId) {
        setCurrentChatId(null);
        setMessages([]);
      }
      
      await fetchChats();
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast({
        title: "Error",
        description: "Failed to delete chat",
        type: "error"
      });
    }
  };

  const detectContentCategory = (message: string): string | null => {
    message = message.toLowerCase();
    
    if (message.includes("meal plan") || 
        message.includes("diet") || 
        message.includes("nutrition") || 
        message.includes("food") || 
        message.includes("recipe") || 
        message.includes("calories")) {
      return "nutrition";
    }
    
    if (message.includes("workout") || 
        message.includes("exercise") || 
        message.includes("training") || 
        message.includes("fitness") || 
        message.includes("cardio") || 
        message.includes("strength")) {
      return "exercise";
    }
    
    if (message.includes("supplement") || 
        message.includes("vitamin") || 
        message.includes("mineral") || 
        message.includes("protein") || 
        message.includes("creatine")) {
      return "supplements";
    }
    
    if (message.includes("meditation") || 
        message.includes("mindfulness") || 
        message.includes("stress") || 
        message.includes("anxiety") || 
        message.includes("mental health") || 
        message.includes("breathing")) {
      return "mindfulness";
    }
    
    if (message.includes("sleep") || 
        message.includes("insomnia") || 
        message.includes("rest") || 
        message.includes("bedtime") || 
        message.includes("nap")) {
      return "sleep";
    }
    
    return null;
  };

  const sendMessage = async (content: string, saveToDashboard: boolean = false) => {
    if (!user || !content.trim() || isLoading) return;
    
    setError(null);
    setIsLoading(true);
    
    const tempId = Date.now().toString();
    
    const userMessage: ChatMessage = {
      id: tempId,
      content,
      role: "user",
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      let chatId = currentChatId;
      if (!chatId) {
        chatId = await createNewChat();
        if (!chatId) throw new Error("Failed to create chat session");
      }
      
      const messageHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("You must be logged in to use Vita");
      }
      
      const category = detectContentCategory(content);
      
      const { data, error } = await supabase.functions.invoke('chat-vita', {
        body: { 
          messages: [...messageHistory, { role: "user", content }],
          chatId,
          category
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        console.error('Error calling chat function:', error);
        throw new Error(error.message || "Error connecting to Vita");
      }
      
      if (data.error) {
        console.error('Error in chat response:', data.error);
        throw new Error(data.error);
      }
      
      await fetchChats();
      
      if (data.credits) {
        setCredits(data.credits);
      }
      
      const responseMessage: ChatMessage = {
        id: Date.now().toString(),
        content: data.response,
        role: "assistant",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, responseMessage]);
      
      // Auto-detect and save content to the appropriate dashboard section
      if (category && data.response) {
        // Extract a title from the first sentence or use a default
        let title = content.split('.')[0].trim();
        if (title.length > 50) {
          title = title.substring(0, 47) + '...';
        }
        
        const vitaContent: VitaContent = {
          title: title,
          content: data.response,
          category: category as any
        };
        
        // Auto-save content that matches a category
        await saveVitaContent(vitaContent);
        
        toast({
          title: "Content Saved",
          description: `This content has been saved to your ${category} dashboard`,
          type: "success"
        });
      }
      
      return responseMessage;
    } catch (error: any) {
      console.error('Error in chat:', error);
      setError(error.message || "An unexpected error occurred");
      toast({
        title: "Chat Error",
        description: error.message || "Error connecting to Vita",
        type: "error"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    
    const fetchCredits = async () => {
      try {
        const { data, error } = await supabase
          .from('vita_credits')
          .select('credits_used, credits_limit')
          .eq('user_id', user.id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setCredits({
            used: data.credits_used,
            limit: data.credits_limit
          });
        }
      } catch (error) {
        console.error("Error fetching credit usage:", error);
      }
    };

    fetchCredits();
    fetchChats();

    const channel = supabase
      .channel('vita-credits-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'vita_credits',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Credits updated:', payload);
          if (payload.new) {
            setCredits({
              used: payload.new.credits_used,
              limit: payload.new.credits_limit
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    messages,
    isLoading,
    error,
    chats,
    currentChatId,
    credits,
    sendMessage,
    fetchMessages,
    createNewChat,
    updateChatTitle,
    deleteChat,
    fetchChats,
    detectContentCategory
  };
}
